'use es6';

import styled from 'foundations-theming/utils';
import I18n from 'I18n';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import { Button } from 'foundations-components/transitional/components/Button';
import { Small } from 'foundations-components/transitional/components/Small';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { parseFilterLogic } from './filter-parser';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExpressionText = styled.span.withConfig({
  displayName: "LogicEditor__ExpressionText",
  componentId: "x2eahp-0"
})(({
  theme: _theme
}) => ({
  padding: '9px 10px',
  borderRadius: _theme.borderRadius['100'],
  border: `${_theme.borderWidth['100']} solid ${_theme.color['border-core-default']}`,
  fontSize: '16px',
  backgroundColor: _theme.color['fill-field-default'],
  color: _theme.color['text-core-default'],
  display: 'block',
  minHeight: '40px',
  lineHeight: '22px',
  textAlign: 'left',
  verticalAlign: 'middle',
  cursor: 'pointer'
}));
const ErroredExpressionTest = styled.span.withConfig({
  displayName: "LogicEditor__ErroredExpressionTest",
  componentId: "x2eahp-1"
})(({
  theme: _theme
}) => ({
  padding: '9px 10px',
  borderRadius: _theme.borderRadius['100'],
  border: `${_theme.borderWidth['100']} solid ${_theme.color['border-alert-default']}`,
  fontSize: '16px',
  backgroundColor: _theme.color['fill-field-default'],
  color: _theme.color['text-core-default'],
  display: 'block',
  minHeight: '40px',
  lineHeight: '22px',
  textAlign: 'left',
  verticalAlign: 'middle',
  cursor: 'pointer',
  boxShadow: `0 0 0 ${_theme.borderWidth['100']} ${_theme.color['border-alert-default']}`
}));
const Leaf = ({
  value,
  tooltip
}) => tooltip ? /*#__PURE__*/_jsx(Tooltip, {
  title: tooltip,
  placement: "top right",
  children: /*#__PURE__*/_jsx(View, {
    as: "span",
    paddingBlock: "0",
    paddingInline: "25",
    children: value
  })
}) : /*#__PURE__*/_jsx(View, {
  as: "span",
  paddingBlock: "0",
  paddingInline: "25",
  children: value
});
const Paren = ({
  value
}) => /*#__PURE__*/_jsxs("span", {
  children: ["(", value, ")"]
});
const Not = ({
  value
}) => /*#__PURE__*/_jsxs("span", {
  children: [/*#__PURE__*/_jsx(Text, {
    textTransform: "uppercase",
    color: "fill-alert-pressed",
    as: "strong",
    marginBlock: "0",
    marginInline: "100",
    children: "NOT"
  }), value]
});
const And = ({
  left,
  right
}) => /*#__PURE__*/_jsxs("span", {
  children: [left, ' ', /*#__PURE__*/_jsx(Text, {
    color: "text-core-disabled",
    as: "strong",
    marginBlock: "0",
    marginInline: "100",
    children: "AND"
  }), ' ', right]
});
const Or = ({
  left,
  right
}) => /*#__PURE__*/_jsxs("span", {
  children: [left, ' ', /*#__PURE__*/_jsx(Text, {
    color: "text-core-disabled",
    as: "strong",
    marginBlock: "0",
    marginInline: "100",
    children: "OR"
  }), ' ', right]
});
const PRETTY_PRINTING = {
  NOT: Not,
  AND: And,
  OR: Or
};
const prettyPrint = (expression, parentOperator, filterMap) => {
  if (typeof expression === 'string') {
    return /*#__PURE__*/_jsx(Leaf, {
      value: expression,
      tooltip: filterMap ? filterMap.get(expression) || I18n.text('reporting-snowflake.filter-editor.logic-editor.unknown-filter') : null
    });
  }
  const {
    operator,
    value,
    left,
    right
  } = expression;
  const Print = PRETTY_PRINTING[operator] || (() => operator);
  const result = /*#__PURE__*/_jsx(Print, {
    value: value && prettyPrint(value, operator, filterMap),
    left: left && prettyPrint(left, operator, filterMap),
    right: right && prettyPrint(right, operator, filterMap)
  });
  if (parentOperator === operator || !parentOperator) {
    return result;
  }
  return /*#__PURE__*/_jsx(Paren, {
    value: result
  });
};
const tryParse = (maybeExpressionText, validIds) => {
  try {
    return {
      parsed: parseFilterLogic(maybeExpressionText, validIds)
    };
  } catch (error) {
    return {
      error
    };
  }
};
const EditorFormControl = ({
  children,
  error
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    error: error,
    validationMessage: error,
    label: I18n.text('reporting-snowflake.filter-editor.logic-editor.title'),
    tooltip: I18n.text('reporting-snowflake.filter-editor.logic-editor.tooltip', {
      example: '1 and (2 or 3)'
    }),
    tooltipPlacement: "right",
    children: children
  });
};
const LogicEditor = ({
  value = '',
  validIds,
  filterMap,
  onChange
}) => {
  const inputRef = useRef();
  const expression = useMemo(() => tryParse(value, validIds), [value, validIds]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    value,
    expression
  }));
  useEffect(() => {
    setDraft({
      value,
      expression
    });
    setEditing(false);
  }, [value, expression]);
  useEffect(() => {
    if (editing) {
      const element = inputRef.current;
      element.focus();
      element.setSelectionRange(element.value.length, element.value.length);
    }
  }, [editing]);
  const handleDebouncedChange = useMemo(() => debounce(nextValue => {
    setDraft(previous => Object.assign({}, previous, {
      expression: tryParse(nextValue, validIds)
    }));
  }, 300), [validIds]);
  const handleChange = ({
    target: {
      value: nextValue
    }
  }) => {
    setDraft(previous => Object.assign({}, previous, {
      value: nextValue
    }));
    handleDebouncedChange(nextValue);
  };
  const handleApply = () => {
    onChange(draft);
    setEditing(false);
  };
  const handleCancel = () => {
    setDraft({
      value,
      expression
    });
    setEditing(false);
  };
  const handleEdit = () => {
    setDraft({
      value,
      expression
    });
    setEditing(true);
  };
  const {
    value: draftValue,
    expression: draftExpression
  } = draft;
  const hasError = draftValue !== '' && draftExpression && !!draftExpression.error;
  const TextComponent = hasError ? ErroredExpressionTest : ExpressionText;
  const renderErrorMessage = () => hasError ? /*#__PURE__*/_jsx(Small, {
    use: "error",
    marginBlockStart: "100",
    display: "block",
    children: draftExpression.error.message
  }) : null;
  return editing ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(EditorFormControl, {
      error: renderErrorMessage(),
      children: /*#__PURE__*/_jsx(ExpandingTextArea, {
        inputRef: inputRef,
        value: draftValue,
        onChange: handleChange,
        error: hasError
      })
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "300",
      as: "div",
      children: [/*#__PURE__*/_jsx(Button, {
        size: "small",
        use: "tertiary",
        onClick: handleApply,
        children: I18n.text('reporting-snowflake.filter-editor.logic-editor.apply')
      }), /*#__PURE__*/_jsx(Button, {
        size: "small",
        use: "tertiary-light",
        onClick: handleCancel,
        children: I18n.text('reporting-snowflake.filter-editor.logic-editor.cancel')
      })]
    })]
  }) : /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx(EditorFormControl, {
      error: renderErrorMessage(),
      children: /*#__PURE__*/_jsx(TextComponent, {
        onClick: handleEdit,
        error: hasError,
        children: expression && expression.parsed ? prettyPrint(expression.parsed, undefined, filterMap) : value === '' ? I18n.text('reporting-snowflake.filter-editor.logic-editor.all-data') : value
      })
    })
  });
};
export default LogicEditor;